import React, { useState, useEffect, useRef } from "react";
import { X, Printer, RefreshCw, TrendingDown, TrendingUp, Wallet } from "lucide-react";
import api from "../utils/api";
import { todayLocal } from "../utils/dates";
import { formatIndianCurrency } from "../utils/Const";

const METHOD_LABELS = { CASH: "Cash", COD: "Cash (COD)", UPI: "UPI", CARD: "Card", WALLET: "Wallet", SPLIT: "Split" };
const METHOD_COLORS = { CASH: "text-emerald-700", COD: "text-emerald-700", UPI: "text-blue-700", CARD: "text-indigo-700", WALLET: "text-purple-700", SPLIT: "text-teal-700" };

export default function ClosingReportModal({ onClose }) {
  const today = todayLocal();
  const [date, setDate] = useState(today);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const printRef = useRef(null);

  const fetchReport = async (d) => {
    setLoading(true);
    try {
      const res = await api.get(`/reports/daily-closing?date=${d}`);
      setReport(res.data);
    } catch {
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(date); }, [date]);

  const handlePrint = () => {
    if (!printRef.current || !report) return;
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>Daily Closing Report — ${report.date}</title>
      <style>body{font-family:sans-serif;font-size:13px;padding:20px;color:#111;}
      h2{margin:0 0 4px;font-size:18px;}p{margin:2px 0;}
      table{width:100%;border-collapse:collapse;margin-top:12px;}
      th,td{padding:6px 10px;text-align:left;border-bottom:1px solid #eee;}
      th{background:#f5f5f5;font-weight:700;}
      .right{text-align:right;}.highlight{font-weight:900;font-size:16px;}
      .section{margin-top:16px;}.divider{border-top:1px dashed #ccc;margin:12px 0;}
      @media print{button{display:none}}</style>
    </head><body>`);
    win.document.write(printRef.current.innerHTML);
    win.document.write("</body></html>");
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 300);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center">
              <Wallet size={15} className="text-emerald-600" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-800">Daily Closing Report</h2>
              <p className="text-[10px] text-slate-400">End-of-day cash settlement</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={date}
              max={today}
              onChange={e => setDate(e.target.value)}
              className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
            <button onClick={() => fetchReport(date)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
              <RefreshCw size={13} />
            </button>
            <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw size={18} className="animate-spin text-slate-400" />
            </div>
          ) : !report ? (
            <p className="text-xs text-slate-400 text-center py-8">Failed to load report</p>
          ) : (
            <div ref={printRef} className="space-y-4">
              <div className="text-center mb-2">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Closing Report — {report.date}</p>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 text-center">
                  <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide">Bills</p>
                  <p className="text-xl font-black text-slate-800">{report.totalBills}</p>
                </div>
                <div className="bg-teal-50 border border-teal-100 rounded-xl p-3 text-center">
                  <p className="text-[10px] text-teal-600 font-semibold uppercase tracking-wide">Revenue</p>
                  <p className="text-lg font-black text-teal-700">₹{formatIndianCurrency(report.totalRevenue)}</p>
                </div>
                <div className={`rounded-xl p-3 text-center border ${report.netCashInHand >= 0 ? "bg-emerald-50 border-emerald-100" : "bg-rose-50 border-rose-100"}`}>
                  <p className={`text-[10px] font-semibold uppercase tracking-wide ${report.netCashInHand >= 0 ? "text-emerald-600" : "text-rose-500"}`}>Net Cash</p>
                  <p className={`text-lg font-black ${report.netCashInHand >= 0 ? "text-emerald-700" : "text-rose-600"}`}>₹{formatIndianCurrency(Math.abs(report.netCashInHand))}</p>
                </div>
              </div>

              {/* Revenue Breakdown */}
              <div className="rounded-xl border border-slate-100 overflow-hidden">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-100">
                  <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Revenue by payment method</p>
                </div>
                <div className="divide-y divide-slate-50">
                  {Object.entries(report.byMethod)
                    .filter(([, v]) => v > 0)
                    .map(([method, amount]) => (
                      <div key={method} className="flex items-center justify-between px-3 py-2">
                        <span className="text-xs font-semibold text-slate-600">{METHOD_LABELS[method] || method}</span>
                        <span className={`text-sm font-bold ${METHOD_COLORS[method] || "text-slate-700"}`}>₹{formatIndianCurrency(amount)}</span>
                      </div>
                    ))}
                  <div className="flex items-center justify-between px-3 py-2 bg-slate-50">
                    <span className="text-xs font-bold text-slate-700">Total Revenue</span>
                    <span className="text-sm font-black text-teal-700">₹{formatIndianCurrency(report.totalRevenue)}</span>
                  </div>
                </div>
              </div>

              {/* Tax & Discount */}
              <div className="rounded-xl border border-slate-100 overflow-hidden">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-100">
                  <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Tax & Discounts</p>
                </div>
                <div className="divide-y divide-slate-50">
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="text-xs text-slate-600">Subtotal (before discounts)</span>
                    <span className="text-xs font-semibold text-slate-700">₹{formatIndianCurrency(report.totalSubtotal)}</span>
                  </div>
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="flex items-center gap-1 text-xs text-slate-600"><TrendingDown size={11} className="text-rose-400" /> Total Discount given</span>
                    <span className="text-xs font-semibold text-rose-600">- ₹{formatIndianCurrency(report.totalDiscount)}</span>
                  </div>
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="flex items-center gap-1 text-xs text-slate-600"><TrendingUp size={11} className="text-blue-400" /> GST Collected</span>
                    <span className="text-xs font-semibold text-blue-600">+ ₹{formatIndianCurrency(report.totalGst)}</span>
                  </div>
                  {report.totalPlatformFee > 0 && (
                    <div className="flex items-center justify-between px-3 py-2">
                      <span className="text-xs text-slate-600">Platform Fee Collected</span>
                      <span className="text-xs font-semibold text-orange-600">+ ₹{formatIndianCurrency(report.totalPlatformFee)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Expenses */}
              <div className="rounded-xl border border-slate-100 overflow-hidden">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-100">
                  <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Expenses today</p>
                </div>
                {report.expenses.items.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-3">No expenses recorded</p>
                ) : (
                  <div className="divide-y divide-slate-50">
                    {report.expenses.items.map((exp, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2">
                        <div>
                          <p className="text-xs font-semibold text-slate-700">{exp.title}</p>
                          <p className="text-[10px] text-slate-400">{exp.category} · {exp.paymentMode === "credit" ? "Credit" : "Cash"}</p>
                        </div>
                        <span className="text-xs font-bold text-rose-600">- ₹{formatIndianCurrency(exp.price)}</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between px-3 py-2 bg-slate-50">
                      <span className="text-xs font-bold text-slate-700">Total Expenses</span>
                      <span className="text-xs font-black text-rose-600">- ₹{formatIndianCurrency(report.expenses.total)}</span>
                    </div>
                    <div className="flex items-center justify-between px-3 py-2">
                      <span className="text-xs text-slate-500">Cash expenses</span>
                      <span className="text-xs font-semibold text-rose-500">- ₹{formatIndianCurrency(report.expenses.cash)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Online Platform Breakdown */}
              {report.platformBreakdown && Object.keys(report.platformBreakdown).length > 0 && (
                <div className="rounded-xl border border-slate-100 overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 border-b border-slate-100">
                    <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Online orders by platform</p>
                  </div>
                  {Object.entries(report.platformBreakdown).map(([plat, data]) => {
                    const colors = { SWIGGY: "text-orange-600", ZOMATO: "text-red-600", MANUAL: "text-slate-600" };
                    return (
                      <div key={plat} className="flex items-center justify-between px-3 py-2 border-b border-slate-50 last:border-0">
                        <div>
                          <span className={`text-xs font-bold ${colors[plat] || "text-slate-700"}`}>{plat}</span>
                          <span className="text-[10px] text-slate-400 ml-2">{data.count} order{data.count !== 1 ? "s" : ""}</span>
                        </div>
                        <span className={`text-sm font-bold ${colors[plat] || "text-slate-700"}`}>₹{formatIndianCurrency(data.revenue)}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Net Cash Summary */}
              <div className={`rounded-xl border-2 p-4 ${report.netCashInHand >= 0 ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`}>
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-2">Cash settlement summary</p>
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-600">Cash collected (Cash + COD)</span>
                    <span className="font-bold text-emerald-700">+ ₹{formatIndianCurrency(report.cashCollected)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-600">Cash expenses paid</span>
                    <span className="font-bold text-rose-600">- ₹{formatIndianCurrency(report.expenses.cash)}</span>
                  </div>
                  <div className={`flex justify-between text-sm font-black pt-1.5 border-t ${report.netCashInHand >= 0 ? "border-emerald-200 text-emerald-800" : "border-rose-200 text-rose-700"}`}>
                    <span>Net Cash in Hand</span>
                    <span>₹{formatIndianCurrency(Math.abs(report.netCashInHand))}{report.netCashInHand < 0 ? " (deficit)" : ""}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs font-semibold bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200">
            Close
          </button>
          <button
            onClick={handlePrint}
            disabled={!report || loading}
            className="px-5 py-2 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl disabled:opacity-40 flex items-center gap-1.5 transition-colors"
          >
            <Printer size={12} />
            Print Report
          </button>
        </div>
      </div>
    </div>
  );
}
